import { Link } from '@tanstack/react-router';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import { useFilingChain } from '@/hooks/queries/useFinanceReports';
import { cn } from '@/lib/utils';
import { formatDate } from '../utils';
import { isPostFiling, statusBadge } from './reportUtils';

/**
 * The original filing and each amendment of it, so a treasurer can move between the versions of
 * one report. Renders nothing until there are two versions to move between.
 */
export function FilingHistory({ report }: { report: ComplianceReportDetailDto }) {
  const hasHistory = isPostFiling(report.status) || report.isAmendment;
  const { data } = useFilingChain(hasHistory ? report.id : null);

  const entries = data?.chain ?? [];

  if (entries.length < 2) return null;

  return (
    <section aria-labelledby="filing-history-heading" className="rounded-lg border p-4">
      <h3 id="filing-history-heading" className="text-sm font-semibold">
        Filing history
      </h3>
      <ol className="mt-2 space-y-1">
        {entries.map((entry) => {
          const isViewing = entry.complianceReportId === report.id;
          const badge = statusBadge(entry);
          return (
            <li key={entry.complianceReportId}>
              <Link
                to="/admin/finance/reports/$reportId"
                params={{ reportId: entry.complianceReportId }}
                aria-current={isViewing ? 'page' : undefined}
                className={cn(
                  'flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent',
                  isViewing && 'bg-muted font-medium',
                )}
              >
                <span className="underline-offset-2 hover:underline">
                  {entry.amendmentNumber === 0 ? 'Original' : `Amendment #${entry.amendmentNumber}`}
                </span>
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}>
                  {badge.label}
                </span>
                {entry.filedDate ? (
                  <span className="text-xs text-muted-foreground">filed {formatDate(entry.filedDate)}</span>
                ) : (
                  entry.submissionPackageId === null && (
                    <span className="text-xs text-muted-foreground">not yet generated</span>
                  )
                )}
                {isViewing && <span className="text-xs text-muted-foreground">Viewing</span>}
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
